/**
 * A plan, from the owner's side: what Brunel is waiting for, and what it needs him for.
 *
 * The plan itself lives in the database and is moved along by three different parties - the
 * owner states the goal, a planner on the Worker's computer drafts and later judges, and the
 * controller carries the instruction. None of them can see the whole thing at once, so this
 * is the one place that reads a plan's row and says what should happen next on this screen.
 *
 * Pure, so the rules can be tested without a browser or a model. The two that matter:
 * an instruction the planner marked as needing the owner is never sent without him, and a
 * proposed next step is only ever shown - approving it makes a new plan, it does not continue
 * this one.
 */
import type { CommandPlanRow } from './data';

export type PlanStep =
  | { readonly kind: 'wait'; readonly why: string }
  | { readonly kind: 'needs_approval' }
  | { readonly kind: 'send' }
  | { readonly kind: 'observe' }
  | { readonly kind: 'finished'; readonly outcome: 'completed' | 'needs_owner' | 'failed' | 'withdrawn' };

/** How far the controller has got with the one instruction this plan carries. */
export interface RunProgress {
  /** True once the controller has written the instruction into the terminal. */
  readonly sent: boolean;
  /** True once the tool has answered and settled, so there is something to read. */
  readonly settled: boolean;
}

/**
 * What this screen should do about a plan.
 *
 * `approved` is the owner having pressed send on an instruction the planner wanted him to see.
 */
export function nextPlanStep(plan: CommandPlanRow, progress: RunProgress, approved: boolean): PlanStep {
  switch (plan.status) {
    case 'drafting':
      return { kind: 'wait', why: 'the planner is working out what to ask' };
    case 'proposed':
      if (plan.instruction === null) return { kind: 'wait', why: 'the planner is working out what to ask' };
      if (plan.session_id === null) return { kind: 'wait', why: 'that Worker has no terminal open' };
      if (plan.requires_approval && !approved) return { kind: 'needs_approval' };
      return { kind: 'send' };
    case 'sent':
      if (!progress.sent) return { kind: 'wait', why: 'sending' };
      if (plan.terminal_status === 'waiting_for_approval') {
        // The tool is asking the owner something. Reading it as a result would be reading a
        // question as an answer, so the plan waits for him rather than judging half of it.
        return { kind: 'wait', why: 'the tool is asking you for permission' };
      }
      if (!progress.settled) return { kind: 'wait', why: 'the Worker is answering' };
      return { kind: 'observe' };
    case 'evaluating':
      return { kind: 'wait', why: 'the planner is judging what came back' };
    case 'completed':
      return { kind: 'finished', outcome: 'completed' };
    case 'needs_owner':
      return { kind: 'finished', outcome: 'needs_owner' };
    case 'failed':
      return { kind: 'finished', outcome: 'failed' };
    case 'withdrawn':
      return { kind: 'finished', outcome: 'withdrawn' };
    default:
      return { kind: 'wait', why: 'waiting' };
  }
}

/** What the owner is told about a plan, in one line. */
export function planLine(plan: CommandPlanRow): string {
  switch (plan.status) {
    case 'drafting': return 'Working out what to ask this Worker…';
    case 'proposed': return plan.requires_approval
      ? 'Drafted. It wants your approval before it is sent.'
      : 'Drafted. Sending it to the Worker…';
    case 'sent': return 'Sent. Waiting for the Worker to answer…';
    case 'evaluating': return 'Judging what came back…';
    case 'completed': return `Done: ${plan.result_summary ?? 'the goal was met'}`;
    case 'needs_owner': return `Over to you: ${plan.result_summary ?? 'this needs a decision'}`;
    case 'failed': return `Failed: ${plan.failure ?? plan.result_summary ?? 'something went wrong'}`;
    case 'withdrawn': return 'Stopped by you.';
    default: return plan.status;
  }
}

/** A plan is still moving; the screen should keep looking at it. */
export function isOpen(plan: CommandPlanRow): boolean {
  return ['drafting', 'proposed', 'sent', 'evaluating'].includes(plan.status);
}

/** A next step the owner can approve. Only ever shown; approving it starts a NEW plan. */
export function proposedNext(plan: CommandPlanRow): string | null {
  if (plan.proposed_next_instruction === null) return null;
  if (plan.status !== 'needs_owner' && plan.status !== 'completed') return null;
  return plan.proposed_next_instruction;
}
